import { useRef } from "react";
import { api, getToken } from "../lib/api";

/** Must match backend ALLOWED_EXTENSIONS (apps/backend/app/core/config.py). */
export const UPLOAD_ACCEPT = ".pdf,.md,.docx,.txt,.csv,.xlsx";
export const UPLOAD_TYPES_LABEL = "PDF, Markdown, DOCX, TXT, CSV, or XLSX";
/** Must match backend MAX_FILE_SIZE_MB (apps/backend/app/core/config.py). */
export const MAX_UPLOAD_MB = 50;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

const ALLOWED_EXT = new Set(["pdf", "md", "docx", "txt", "csv", "xlsx"]);

function fileExtension(name) {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

/**
 * KB selector + upload button. Used on Knowledge and Documents pages.
 */
export function KnowledgeUploadBar({
  toast,
  page,
  live,
  kbs = [],
  kbId,
  onKbIdChange,
  onUploaded,
  compact = false,
}) {
  const fileRef = useRef(null);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!getToken()) {
      toast("Sign in with email to upload.", "info");
      e.target.value = "";
      return;
    }
    if (!live) {
      toast("Knowledge Bases are in demo mode — sign in and wait for Live data before uploading.", "info");
      e.target.value = "";
      return;
    }
    const ext = fileExtension(file.name);
    if (!ALLOWED_EXT.has(ext)) {
      toast(`Unsupported file type (.${ext || "?"}). Allowed: ${UPLOAD_TYPES_LABEL}.`, "info");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      toast(`File too large (${sizeMb} MB). Maximum upload size is ${MAX_UPLOAD_MB} MB.`, "info");
      e.target.value = "";
      return;
    }
    const targetKb = kbId || kbs[0]?.id;
    if (!targetKb) {
      toast("Create a Knowledge Base first", "info");
      e.target.value = "";
      return;
    }
    try {
      await api.uploadDocument({ file, kb_id: targetKb });
      toast(`${file.name} uploaded — indexing started`, "success");
      await onUploaded?.();
    } catch (err) {
      toast(`Upload failed: ${err.message}`, "info");
    }
    e.target.value = "";
  }

  return (
    <div className={`flex gap-8${compact ? "" : ""}`} style={{ flexWrap: "wrap", alignItems: "center" }}>
      {live && kbs.length > 0 && (
        <select
          className="input-full"
          style={{ width: compact ? 180 : 200, height: 38 }}
          value={kbId}
          onChange={(e) => onKbIdChange?.(e.target.value)}
        >
          {kbs.map((k) => (
            <option key={k.id} value={k.id}>
              {k.emoji || "📘"} {k.name}
            </option>
          ))}
        </select>
      )}
      <input
        ref={fileRef}
        type="file"
        accept={UPLOAD_ACCEPT}
        style={{ display: "none" }}
        onChange={handleUpload}
      />
      <button type="button" className="btn btn-primary" onClick={() => fileRef.current?.click()}>
        ⬆ Upload SOP / Runbook
      </button>
      {!compact && (
        <span className="text-xs text-muted" style={{ maxWidth: 280, lineHeight: 1.45 }}>
          {UPLOAD_TYPES_LABEL} · max {MAX_UPLOAD_MB} MB per file
        </span>
      )}
    </div>
  );
}
